import { APIHandler } from "utilities/apiHandler";
import { type ListEntityType } from "utilities/list/list.types";

import {
    GridViewDeleteResponse,
    GridViewSavedResponse,
    GridViewsResponse,
    type IGridViewSaveRequest,
    SetDefaultViewResponse,
} from "commonComponents/utilities/Grid/common/GridSettings/GridSettings.api.types";

export interface IGridSettingsHandler {
    getGridViews(
        type: ListEntityType,
        isBTAdmin?: boolean,
        jobs?: number[],
    ): Promise<GridViewsResponse>;
    createView(req: IGridViewSaveRequest, jobs?: number[]): Promise<GridViewSavedResponse>;
    updateView(
        id: number,
        req: IGridViewSaveRequest,
        jobs?: number[],
    ): Promise<GridViewSavedResponse>;
    deleteView(id: number, jobs?: number[]): Promise<GridViewDeleteResponse>;
    setDefaultView(
        id: number,
        gridType: ListEntityType,
        jobs?: number[],
    ): Promise<SetDefaultViewResponse>;
}

export interface IGridSettingsExternalHandler {
    getGridViewsExternal(type: ListEntityType, shareToken?: string): Promise<GridViewsResponse>;
}

export class GridSettingsHandler implements IGridSettingsHandler {
    async getGridViews(
        type: ListEntityType,
        isBTAdmin?: boolean,
        jobs?: number[],
    ): Promise<GridViewsResponse> {
        if (jobs) {
            return await APIHandler("/api/GridViews/GetGridViews", {
                method: "POST",
                data: { type: type, isInReact: true, jobs: jobs },
                isBTAdmin,
                responseType: GridViewsResponse,
            });
        }
        return await APIHandler("/api/GridViews/", {
            method: "GET",
            data: { id: type, isInReact: true },
            responseType: GridViewsResponse,
        });
    }
    async createView(req: IGridViewSaveRequest, jobs?: number[]): Promise<GridViewSavedResponse> {
        return await APIHandler("/api/GridViews", {
            method: "POST",
            data: { jobs: jobs, saveRequest: req },
            responseType: GridViewSavedResponse,
        });
    }
    async updateView(
        id: number,
        req: IGridViewSaveRequest,
        jobs?: number[],
    ): Promise<GridViewSavedResponse> {
        return await APIHandler(`/api/GridViews/${id}`, {
            method: "PUT",
            data: { jobs: jobs, saveRequest: req },
            responseType: GridViewSavedResponse,
        });
    }
    async deleteView(id: number, jobs?: number[]): Promise<GridViewDeleteResponse> {
        return await APIHandler(`/api/GridViews/${id}`, {
            method: "DELETE",
            data: { isInReact: true, jobs: jobs },
            responseType: GridViewDeleteResponse,
        });
    }

    async setDefaultView(
        id: number,
        gridType: ListEntityType,
        jobs?: number[],
    ): Promise<SetDefaultViewResponse> {
        return await APIHandler(`/api/GridViews/${id}/SetDefault`, {
            method: "PUT",
            data: { type: gridType, isInReact: true, jobs: jobs },
            responseType: SetDefaultViewResponse,
        });
    }
}

export class GridSettingsExternalHandler implements IGridSettingsExternalHandler {
    async getGridViewsExternal(
        type: ListEntityType,
        shareToken?: string,
    ): Promise<GridViewsResponse> {
        return await APIHandler(`/api/GridViews/GetGridViewsExternal?&shareToken=${shareToken}`, {
            method: "POST",
            data: { type: type, isInReact: true },
            responseType: GridViewsResponse,
        });
    }
}

export const defaultGridSettingsHandler = new GridSettingsHandler();
